"use client";

import { Text } from "@stone-payments/jade";
import { type GetProductListOutput } from "@/actions/schemas/products";
import { Skeleton } from "@/components/skeleton";
import { cn } from "@/utils/classnames";
import { Mask } from "@/utils/masks";

type Props = React.HTMLAttributes<HTMLDivElement> & {
  layout?: "small" | "large";
  variants: GetProductListOutput["items"][number]["variants"];
  loading?: boolean;
};

export const ProductPrice = ({
  variants,
  className,
  layout = "small",
  loading = false,
  ...rest
}: Props) => {
  if (loading)
    return (
      <Skeleton
        height={layout === "small" ? "h-5" : "h-8"}
        width={layout === "small" ? "w-20" : "w-32"}
      />
    );

  const minPrice = variants?.reduce(
    (acc, item) => (acc > item?.price?.sale ? item.price.sale : acc),
    variants[0]?.price?.sale
  );

  const hasVariants = variants?.length > 1;
  const priceBRL = Mask.formatToBrlCurrency(minPrice || 0);

  return (
    <div
      className={cn(
        {
          "inline-flex items-end gap-x-1": layout === "small",
          "flex flex-col": layout === "large",
        },
        className
      )}
      itemProp="offers"
      itemScope
      itemType="https://schema.org/Offer"
      {...rest}
    >
      {hasVariants && (
        <Text
          className="!leading-4.5"
          color="medium"
          variant="text-small"
          weight="regular"
        >
          a partir de
        </Text>
      )}

      {layout === "small" && (
        <Text
          color="high"
          variant="text-medium"
          weight="medium"
          itemProp="price"
        >
          {priceBRL}
        </Text>
      )}

      {layout === "large" && (
        <Text variant="heading-small" color="high" itemProp="price">
          {priceBRL}
        </Text>
      )}

      <meta itemProp="priceCurrency" content="BRL" />
    </div>
  );
};
